import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from 'react'
import { translations, type Language, type TranslationKey } from './translations'

interface LanguageContextValue {
  lang: Language
  setLang: (lang: Language) => void
  t: (key: TranslationKey, params?: Record<string, string | number>) => string
  formatStatus: (status: string | undefined | null) => string
  isHindi: boolean
}

const STORAGE_KEY = 'freshgrocer_language'

const STATUS_MAP: Record<string, { en: string; hi: string }> = {
  PLACED: { en: 'Order Placed', hi: 'ऑर्डर प्राप्त हुआ' },
  CONFIRMED: { en: 'Confirmed', hi: 'कन्फर्म हुआ' },
  PACKING: { en: 'Packing Items', hi: 'पैक हो रहा है' },
  READY_FOR_PICKUP: { en: 'Ready for Pickup', hi: 'पिकअप के लिए तैयार' },
  OUT_FOR_DELIVERY: { en: 'Out for Delivery', hi: 'डिलीवरी के लिए निकल गया' },
  DELIVERED: { en: 'Delivered', hi: 'डिलीवर हुआ' },
  CANCELLED: { en: 'Cancelled', hi: 'रद्द' },
  PAID: { en: 'Paid', hi: 'भुगतान सफल' },
  PENDING: { en: 'Pending', hi: 'लंबित' },
  FAILED: { en: 'Failed', hi: 'विफल' },
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved === 'hi' || saved === 'en') {
        return saved
      }
    } catch {
      // Ignore storage access errors
    }
    return 'en'
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // Ignore storage access errors
    }
    document.documentElement.lang = lang
  }, [lang])

  const setLang = (nextLang: Language) => {
    setLangState(nextLang)
  }

  const value = useMemo<LanguageContextValue>(() => {
    const dict = translations[lang] || translations.en

    const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
      let str: string = dict[key] || translations.en[key] || (key as string)
      if (params) {
        Object.entries(params).forEach(([paramKey, paramVal]) => {
          str = str.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramVal))
        })
      }
      return str
    }

    const formatStatus = (status: string | undefined | null): string => {
      if (!status) return '—'
      const match = STATUS_MAP[status]
      if (match) {
        return match[lang] || match.en
      }
      return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    }

    return {
      lang,
      setLang,
      t,
      formatStatus,
      isHindi: lang === 'hi',
    }
  }, [lang])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useTranslation() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider')
  }
  return context
}
